import { CSSProperties, useEffect, useRef, useState } from 'react';
import { previewPhrases } from './previewPhrases';
import { previewPhraseMinimumWidth, previewPhraseMusicXml } from './previewPhraseMusicXml';
import {
  RenderedPreviewPhrase,
  previewStaffSpace,
  renderPreviewPhraseSvg,
} from './renderPreviewPhraseSvg';
import { useElementSize } from './useElementSize';

// Widths are rounded down to this step, and each step is engraved once and remembered.
const WIDTH_STEP = 8;
// How long a resize must rest before the phrase is engraved again at the new width.
const RESIZE_SETTLE_MS = 150;
const MAX_REMEMBERED_RENDERS = 64;

// Thrown for an engraving that was dropped before it started because its level or size
// was no longer the one on screen; not a failure to draw.
class StaleEngraving extends Error {
  constructor() {
    super('The preview phrase was no longer on screen');
    // Keeps `instanceof` working where classes are compiled down to functions.
    Object.setPrototypeOf(this, StaleEngraving.prototype);
  }
}

// Runs `work` once the browser is idle (or soon after an input), never in the middle of
// the tap or resize that asked for it.
const whenIdle = (work: () => void) => {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(work, { timeout: 100 });
  } else {
    setTimeout(work, 0);
  }
};

const renders = new Map<string, Promise<RenderedPreviewPhrase>>();
// For an engraving still waiting for its idle moment: whether anyone who asked for it
// still has it on screen. A level left and returned to before the job runs adds its check
// to that job's list, so the job runs for it instead of being dropped as stale. Each job
// keeps its own list: a newer job under the same key (after the older one fell out of the
// cache) never takes over or empties it.
const waiting = new Map<string, Array<() => boolean>>();

const engrave = (
  levelNumber: number,
  width: number,
  staffSpace: number,
  stillWanted: () => boolean,
) => {
  const key = `${levelNumber}|${width}|${staffSpace.toFixed(3)}`;
  const remembered = renders.get(key);
  if (remembered) {
    waiting.get(key)?.push(stillWanted);
    return remembered;
  }
  const checks = [stillWanted];
  waiting.set(key, checks);
  const phrase = previewPhrases[levelNumber];
  const render = new Promise<RenderedPreviewPhrase>((resolve, reject) => {
    whenIdle(() => {
      if (waiting.get(key) === checks) waiting.delete(key);
      // Flicking through levels: only a level still on screen is engraved.
      if (!checks.some((isWanted) => isWanted())) {
        reject(new StaleEngraving());
        return;
      }
      renderPreviewPhraseSvg(
        previewPhraseMusicXml(phrase),
        phrase.treble.length,
        width,
        staffSpace,
      ).then(resolve, reject);
    });
  });
  // Forget a failed or dropped job, but never a newer one stored under the same key.
  render.catch(() => renders.get(key) === render && renders.delete(key));
  renders.set(key, render);
  if (renders.size > MAX_REMEMBERED_RENDERS) {
    renders.delete(renders.keys().next().value as string);
  }
  return render;
};

interface PreviewPhraseProps {
  levelNumber: number;
  // "Level 9: 2-Part Harmony", read out in place of the notation.
  levelTitle: string;
  // The level's preview picture, shown when there is no notation or it fails to draw.
  fallbackSrc: string;
  // The phrase's height in CSS pixels; it stays this height at every width.
  height: number;
}

interface Engraving {
  levelNumber: number;
  width: number;
  result: RenderedPreviewPhrase;
}

// The level's preview phrase as live notation: it fills the width it is given at a fixed
// height, the notes spreading out or closing up while every symbol keeps its size.
export const PreviewPhrase = ({
  levelNumber,
  levelTitle,
  fallbackSrc,
  height,
}: PreviewPhraseProps) => {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const boxWidth = useElementSize(boxRef, RESIZE_SETTLE_MS).width;
  const staffSpace = previewStaffSpace(height);
  const phrase = previewPhrases[levelNumber];
  const hasNotation = !!phrase;
  // The surfaces hide the preview below the width where it reads; this guards the phrase
  // itself. The box's own width decides, so a box the surface shows always gets notation;
  // the engraving width is then stepped down to the 8px grid, but never below the minimum.
  const minimumWidth = hasNotation ? previewPhraseMinimumWidth(phrase, staffSpace) : 0;
  const fits = height > 0 && hasNotation && boxWidth > 0 && boxWidth >= minimumWidth;
  const width = fits ? Math.max(Math.floor(boxWidth / WIDTH_STEP) * WIDTH_STEP, minimumWidth) : 0;
  const [engraving, setEngraving] = useState<Engraving | null>(null);
  const [failed, setFailed] = useState<{ levelNumber: number; width: number } | null>(null);

  useEffect(() => {
    if (!hasNotation || !fits) return undefined;
    let current = true;
    engrave(levelNumber, width, staffSpace, () => current).then(
      (result) => current && setEngraving({ levelNumber, width, result }),
      (error) => current && !(error instanceof StaleEngraving) && setFailed({ levelNumber, width }),
    );
    return () => {
      current = false;
    };
  }, [levelNumber, width, staffSpace, hasNotation, fits]);

  // Without ResizeObserver the width cannot be followed, so the picture stands in.
  const canFollowWidth = typeof ResizeObserver !== 'undefined';
  const failedHere = failed?.levelNumber === levelNumber && failed.width === width;
  const showsPicture = !hasNotation || failedHere || !canFollowWidth;
  // While a new width is being engraved the last one stays up, so nothing jumps.
  const shown = !showsPicture && engraving?.levelNumber === levelNumber ? engraving : null;

  return (
    <div
      ref={boxRef}
      role="img"
      aria-label={`Preview of ${levelTitle}`}
      data-testid="preview-phrase"
      data-preview={showsPicture ? 'picture' : 'notation'}
      style={{ ...boxStyle, height: `${height}px` }}
    >
      {showsPicture && fallbackSrc && (
        <img src={fallbackSrc} alt="" aria-hidden="true" style={pictureStyle} />
      )}
      {shown && fits && (
        <div
          aria-hidden="true"
          data-width={shown.width}
          style={{ position: 'absolute', left: 0, top: `${shown.result.offsetTop}px` }}
          dangerouslySetInnerHTML={{ __html: shown.result.svg }}
        />
      )}
    </div>
  );
};

const boxStyle: CSSProperties = {
  position: 'relative',
  width: '100%',
  minWidth: 0,
  overflow: 'hidden',
};

const pictureStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  height: '100%',
  objectFit: 'contain',
};
